import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, Check, Copy, ExternalLink, LogOut, Mail, MessageCircle, FileText, Printer, Upload, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const inputCls =
  "w-full rounded-xl border border-[#E5DEC3] bg-white px-4 py-3 text-sm text-[#121A16] outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#0F382A] focus:shadow-[0_0_0_3px_rgba(15,56,42,0.08)]";
const labelCls = "mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-[#4A5A52]";

const TABS = [
  { id: "enquiries", label: "Enquiries", testid: "tab-enquiries" },
  { id: "applications", label: "Job Applications", testid: "tab-applications" },
  { id: "handoffs", label: "AI Handoffs", testid: "tab-handoffs" },
  { id: "availability", label: "Availability", testid: "tab-availability" },
  { id: "settings", label: "Site Settings", testid: "tab-settings" },
  { id: "kit", label: "Marketing Kit", testid: "tab-kit" },
  { id: "password", label: "Password", testid: "tab-password" },
];

const WHATSAPP_TEMPLATE = `Hello {name}, this is Don from Don Sparkle Cleaning in Harlow. Thanks for your enquiry!

Here is our simple price list:

DOMESTIC
- Regular weekly clean: £18/hour
- Deep clean: £160–£200
- End of tenancy: £230–£260 (deposit-back guarantee)
- Oven detailing: from £55

COMMERCIAL
- Small offices: £18–£20/hour
- Medium offices: £20–£22/hour
- Large offices: £22–£25/hour

New customers get 10% off their first clean, plus a FREE site visit and no-obligation quote. Weekly & monthly contracts available, and every job comes with a professional invoice & receipt.

Would you like to book your free site visit? I'm available Mon–Sat, 8am–6pm.

— Don, Don Sparkle Cleaning
07823 945998
Quarry Spring, Harlow CM20 3HS`;

const CONTRACT_TEMPLATE = `CLEANING SERVICE AGREEMENT

This agreement is made on {date} between:

Service Provider: Don Sparkle Cleaning, Quarry Spring, Harlow CM20 3HS ("the Company")
Client: {client name}, of {client address} ("the Client")

1. SERVICES
The Company agrees to provide the following cleaning services:
{describe services, e.g. Regular weekly domestic clean / office clean}
At the following premises: {service address}

2. SCHEDULE
Days and times: {e.g. every Tuesday, 9am–12pm}
Start date: {start date}

3. FEES
Rate: £{rate} per {hour/visit}
Invoices are issued after each {visit/month} and are payable within 7 days by bank transfer. A receipt is provided for every payment.

4. ACCESS & UTILITIES
The Client will provide access to the premises, running water and electricity. Keys or access codes will be kept secure and returned on termination.

5. PRODUCTS & EQUIPMENT
The Company supplies eco-conscious cleaning products and equipment unless otherwise agreed in writing.

6. INSURANCE & GUARANTEE
The Company is fully insured and its cleaners are vetted. If the Client is not satisfied with a clean, the Company will return to re-clean free of charge when notified within 24 hours.

7. CANCELLATION & TERMINATION
Either party may cancel a scheduled visit with at least 24 hours' notice. Visits cancelled with less notice may be charged at 50% of the agreed fee. Either party may end this agreement with 2 weeks' written notice.

8. LIABILITY
Any damage caused by the Company must be reported within 24 hours of the visit. The Company's liability is limited to the cost of repair or replacement.

Signed by the Company: ______________________  Date: __________
Signed by the Client: _______________________  Date: __________`;

const fmtDate = (d) =>
  d ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

const fmtDay = (d) =>
  d ? new Date(d + "T12:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "";

const Empty = ({ children }) => (
  <p className="py-12 text-center text-sm text-[#4A5A52]">{children}</p>
);

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied — paste it anywhere.`);
  } catch {
    toast.error("Copy failed — select the text manually.");
  }
};

function StatusToggle({ id, status, endpoint, auth, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const done = status === "done";
  const toggle = async () => {
    setBusy(true);
    const next = done ? "pending" : "done";
    try {
      await axios.patch(`${API}/${endpoint}/${id}`, { status: next }, auth);
      onChange(id, next);
      toast.success(next === "done" ? "Marked as done." : "Reopened.");
    } catch {
      toast.error("Could not update — please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3">
      <span className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${
        done ? "bg-[#0F382A]/10 text-[#0F382A]" : "bg-[#D4AF37]/20 text-[#8a6d1a]"
      }`}>
        {done ? "Done" : "Pending"}
      </span>
      <button
        data-testid={testid}
        onClick={toggle}
        disabled={busy}
        className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-[background-color,color,opacity] duration-300 disabled:opacity-50 ${
          done
            ? "border border-[#E5DEC3] text-[#4A5A52] hover:border-[#0F382A] hover:text-[#0F382A]"
            : "bg-[#0F382A] text-[#FAF8F5] hover:bg-[#1B523F]"
        }`}
      >
        <Check className="h-3.5 w-3.5" strokeWidth={2} />
        {done ? "Reopen" : "Mark as done"}
      </button>
    </div>
  );
}

function EnquiriesTab({ items, auth, onStatusChange, slots }) {
  if (!items.length) return <Empty>No enquiries yet.</Empty>;
  const slotLabel = (id) => slots.find((s) => s.id === id)?.label || id;
  return (
    <div className="space-y-4">
      {items.map((e) => (
        <div key={e.id} data-testid={`enquiry-${e.id}`} className={`rounded-2xl border border-[#E5DEC3] bg-white p-6 ${e.status === "done" || e.status === "cancelled" ? "opacity-70" : ""}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-semibold text-[#0F382A]">{e.name} <span className="font-normal text-[#4A5A52]">· {e.service}</span></p>
            {e.status === "cancelled" ? (
              <span className="rounded-full bg-red-50 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-red-700">Cancelled</span>
            ) : (
              <StatusToggle id={e.id} status={e.status} endpoint="enquiries" auth={auth} onChange={onStatusChange} testid={`enquiry-done-${e.id}`} />
            )}
          </div>
          <p className="mt-1 text-sm text-[#4A5A52]">{e.phone} · {e.email}</p>
          {e.preferred_date && (
            <p className="mt-2 flex items-center gap-2 text-sm font-medium text-[#1B523F]">
              <CalendarClock className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.75} />
              {fmtDay(e.preferred_date)}{e.preferred_slot ? ` · ${slotLabel(e.preferred_slot)}` : ""}
            </p>
          )}
          {e.estimate && <p className="mt-2 text-sm font-medium text-[#1B523F]">Estimate: {e.estimate}</p>}
          {e.message && <p className="mt-2 text-sm leading-relaxed text-[#4A5A52]">{e.message}</p>}
          <p className="mt-2 font-mono text-[11px] text-[#4A5A52]">{fmtDate(e.created_at)}</p>
        </div>
      ))}
    </div>
  );
}

function ApplicationsTab({ items, auth, onStatusChange }) {
  if (!items.length) return <Empty>No applications yet.</Empty>;
  return (
    <div className="space-y-4">
      {items.map((a) => (
        <div key={a.id} data-testid={`application-${a.id}`} className={`rounded-2xl border border-[#E5DEC3] bg-white p-6 ${a.status === "done" ? "opacity-70" : ""}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-semibold text-[#0F382A]">{a.name} <span className="font-normal text-[#4A5A52]">· {a.role}</span></p>
            <StatusToggle id={a.id} status={a.status} endpoint="applications" auth={auth} onChange={onStatusChange} testid={`application-done-${a.id}`} />
          </div>
          <p className="mt-1 text-sm text-[#4A5A52]">{a.phone} · {a.email}{a.availability ? ` · ${a.availability}` : ""}</p>
          {a.message && <p className="mt-2 text-sm leading-relaxed text-[#4A5A52]">{a.message}</p>}
          <p className="mt-2 font-mono text-[11px] text-[#4A5A52]">{fmtDate(a.created_at)}</p>
        </div>
      ))}
    </div>
  );
}

function HandoffsTab({ items, auth, onStatusChange }) {
  const [busy, setBusy] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  if (!items.length) return <Empty>No AI handoffs yet. When Sparkle can't answer something, it will appear here.</Empty>;

  const isEmail = (c) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((c || "").trim());

  const toggle = async (h) => {
    const next = h.status === "replied" ? "pending" : "replied";
    setBusy(h.session_id);
    try {
      await axios.patch(`${API}/assistant/handoffs/${h.session_id}`, { status: next }, auth);
      onStatusChange(h.session_id, next);
      toast.success(next === "replied" ? "Marked as replied." : "Moved back to pending.");
    } catch {
      toast.error("Could not update — please try again.");
    } finally {
      setBusy(null);
    }
  };

  const sendReply = async (h) => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/assistant/handoffs/${h.session_id}/reply`, { message: replyText }, auth);
      onStatusChange(h.session_id, "replied");
      setReplyFor(null);
      setReplyText("");
      toast.success(`Reply emailed to ${h.contact}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the email — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4">
      {items.map((h) => (
        <div key={h.session_id} data-testid={`handoff-${h.session_id}`} className={`rounded-2xl border border-[#E5DEC3] bg-white p-6 ${h.status === "replied" ? "opacity-70" : ""}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-semibold text-[#0F382A]">
              {h.name || "Visitor"}
              {h.contact && <span className="font-normal text-[#4A5A52]"> · {h.contact}</span>}
            </p>
            <div className="flex items-center gap-3">
              <span className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${
                h.status === "replied"
                  ? "bg-[#0F382A]/10 text-[#0F382A]"
                  : h.status === "pending"
                    ? "bg-[#D4AF37]/20 text-[#8a6d1a]"
                    : "bg-[#F3EFEA] text-[#4A5A52]"
              }`}>
                {h.status === "replied" ? "Replied" : h.status === "pending" ? "Awaiting your reply" : "Waiting for contact details"}
              </span>
              {isEmail(h.contact) && (
                <button
                  data-testid={`handoff-reply-${h.session_id}`}
                  onClick={() => { setReplyFor(replyFor === h.session_id ? null : h.session_id); setReplyText(""); }}
                  className="flex items-center gap-1.5 rounded-full bg-[#D4AF37] px-4 py-2 text-xs font-semibold text-[#0B1D16] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#e3c153]"
                >
                  <Mail className="h-3.5 w-3.5" strokeWidth={2} />
                  Reply by email
                </button>
              )}
              <button
                data-testid={`handoff-resolve-${h.session_id}`}
                onClick={() => toggle(h)}
                disabled={busy === h.session_id}
                className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-[background-color,color,opacity] duration-300 disabled:opacity-50 ${
                  h.status === "replied"
                    ? "border border-[#E5DEC3] text-[#4A5A52] hover:border-[#0F382A] hover:text-[#0F382A]"
                    : "bg-[#0F382A] text-[#FAF8F5] hover:bg-[#1B523F]"
                }`}
              >
                <Check className="h-3.5 w-3.5" strokeWidth={2} />
                {h.status === "replied" ? "Reopen" : "Mark as replied"}
              </button>
            </div>
          </div>
          {h.last_message && (
            <p className="mt-2 text-sm leading-relaxed text-[#4A5A52]">
              <span className="font-medium text-[#121A16]">They asked:</span> “{h.last_message}”
            </p>
          )}
          {h.reply && (
            <p className="mt-2 rounded-lg bg-[#F3EFEA] px-4 py-2.5 text-sm leading-relaxed text-[#4A5A52]">
              <span className="font-medium text-[#121A16]">You replied:</span> {h.reply}
            </p>
          )}
          {replyFor === h.session_id && (
            <div className="mt-4 rounded-xl border border-[#D4AF37]/50 bg-[#FAF8F5] p-4">
              <textarea
                data-testid={`handoff-reply-text-${h.session_id}`}
                rows={3}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={`Write your reply — it will be emailed to ${h.contact}`}
                className="w-full resize-none rounded-lg border border-[#E5DEC3] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0F382A]"
              />
              <div className="mt-3 flex items-center gap-3">
                <button
                  data-testid={`handoff-reply-send-${h.session_id}`}
                  onClick={() => sendReply(h)}
                  disabled={sending || !replyText.trim()}
                  className="rounded-full bg-[#0F382A] px-6 py-2.5 text-xs font-semibold text-[#FAF8F5] transition-[background-color,opacity] duration-300 hover:bg-[#1B523F] disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Send email"}
                </button>
                <button
                  onClick={() => { setReplyFor(null); setReplyText(""); }}
                  className="text-xs text-[#4A5A52] underline underline-offset-2 hover:text-[#0F382A]"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          <p className="mt-2 font-mono text-[11px] text-[#4A5A52]">{fmtDate(h.updated_at)}</p>
        </div>
      ))}
    </div>
  );
}

function AvailabilityTab({ availability, reload, auth }) {
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  if (!availability) return <Empty>Loading availability…</Empty>;

  const block = async () => {
    if (!date) return;
    setBusy(true);
    try {
      await axios.post(`${API}/availability/block`, { date }, auth);
      toast.success(`${fmtDay(date)} blocked — clients can't book it.`);
      setDate("");
      reload();
    } catch {
      toast.error("Could not block that day.");
    } finally {
      setBusy(false);
    }
  };

  const unblock = async (d) => {
    try {
      await axios.delete(`${API}/availability/block/${d}`, auth);
      toast.success(`${fmtDay(d)} is bookable again.`);
      reload();
    } catch {
      toast.error("Could not unblock that day.");
    }
  };

  const upcoming = Object.entries(availability.taken || {})
    .filter(([d]) => d >= new Date().toISOString().split("T")[0])
    .sort(([a], [b]) => a.localeCompare(b));
  const slotLabel = (id) => availability.slots.find((s) => s.id === id)?.label || id;
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];

  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-[#E5DEC3] bg-white p-6 lg:p-8">
        <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#1B523F]">Block a busy day</p>
        <p className="mb-5 text-xs text-[#4A5A52]">Blocked days show as "fully booked" in the booking form.</p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            data-testid="block-date-input"
            type="date"
            min={tomorrow}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={`${inputCls} max-w-xs`}
          />
          <button
            data-testid="block-date-button"
            onClick={block}
            disabled={busy || !date}
            className="rounded-full bg-[#0F382A] px-6 py-3 text-sm font-semibold text-[#FAF8F5] transition-[background-color,opacity] duration-300 hover:bg-[#1B523F] disabled:opacity-50"
          >
            Block this day
          </button>
        </div>
        {availability.busy_days.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2">
            {availability.busy_days.map((d) => (
              <span key={d} className="flex items-center gap-2 rounded-full border border-[#E5DEC3] bg-[#F3EFEA] px-4 py-2 text-sm text-[#4A5A52]">
                {fmtDay(d)}
                <button
                  data-testid={`unblock-${d}`}
                  onClick={() => unblock(d)}
                  className="text-[#8a6d1a] transition-colors duration-200 hover:text-red-600"
                  aria-label={`Unblock ${d}`}
                >
                  <X className="h-3.5 w-3.5" strokeWidth={2} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-[#E5DEC3] bg-white p-6 lg:p-8">
        <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.25em] text-[#1B523F]">Upcoming booked slots</p>
        {upcoming.length === 0 ? (
          <p className="text-sm text-[#4A5A52]">No upcoming bookings with a date and slot yet.</p>
        ) : (
          <div className="space-y-2">
            {upcoming.map(([d, slots]) => (
              <div key={d} data-testid={`booked-${d}`} className="flex flex-wrap items-baseline gap-3 rounded-xl bg-[#FAF8F5] px-4 py-3">
                <span className="text-sm font-semibold text-[#0F382A]">{fmtDay(d)}</span>
                <span className="text-sm text-[#4A5A52]">{slots.map(slotLabel).join("  ·  ")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ImageField({ label, value, onChange, auth, testid }) {
  const [uploading, setUploading] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/uploads`, fd, auth);
      onChange(`${BACKEND}${data.url}`);
      toast.success("Photo uploaded — remember to Save changes below.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — try a JPG or PNG under 10MB.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex items-center gap-3">
        {value ? (
          <img src={value} alt="" className="h-12 w-12 shrink-0 rounded-lg border border-[#E5DEC3] object-cover" />
        ) : null}
        <input
          data-testid={`${testid}-input`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Photo link, or upload from your phone"
          className={inputCls}
        />
        <label
          data-testid={`${testid}-upload`}
          className={`flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full bg-[#0F382A] px-4 py-2.5 text-xs font-semibold text-[#FAF8F5] transition-[background-color,opacity] duration-300 hover:bg-[#1B523F] ${uploading ? "pointer-events-none opacity-50" : ""}`}
        >
          <Upload className="h-3.5 w-3.5" strokeWidth={2} />
          {uploading ? "Uploading…" : "Upload"}
          <input type="file" accept="image/*" className="hidden" onChange={pick} />
        </label>
      </div>
    </div>
  );
}

function SettingsTab({ settings, setSettings, auth }) {
  const [saving, setSaving] = useState(false);
  if (!settings) return <Empty>Loading settings…</Empty>;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, auth);
      toast.success("Settings saved — the website now shows the new details.");
    } catch {
      toast.error("Could not save settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const setAt = (key, i, field, value) => {
    const arr = [...(settings[key] || [])];
    arr[i] = { ...arr[i], [field]: value };
    setSettings({ ...settings, [key]: arr });
  };

  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-[#E5DEC3] bg-white p-6 lg:p-8">
        <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.25em] text-[#1B523F]">Service prices</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {(settings.services || []).map((s, i) => (
            <div key={s.id}>
              <label className={labelCls}>{s.name}</label>
              <input data-testid={`settings-price-${s.id}`} value={s.price_label} onChange={(e) => setAt("services", i, "price_label", e.target.value)} className={inputCls} />
            </div>
          ))}
        </div>
        <p className="mb-5 mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-[#1B523F]">Extras & contracts (shown in the price list)</p>
        <div className="space-y-3">
          {(settings.extras || []).map((x, i) => (
            <div key={i} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <input data-testid={`settings-extra-name-${i}`} value={x.name} onChange={(e) => setAt("extras", i, "name", e.target.value)} className={inputCls} />
              <input data-testid={`settings-extra-price-${i}`} value={x.price_label} onChange={(e) => setAt("extras", i, "price_label", e.target.value)} className={inputCls} />
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-[#E5DEC3] bg-white p-6 lg:p-8">
        <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.25em] text-[#1B523F]">Offer banner</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Offer title</label>
            <input data-testid="settings-offer-title" value={settings.offer?.title || ""} onChange={(e) => setSettings({ ...settings, offer: { ...settings.offer, title: e.target.value } })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Offer subtitle</label>
            <input data-testid="settings-offer-subtitle" value={settings.offer?.subtitle || ""} onChange={(e) => setSettings({ ...settings, offer: { ...settings.offer, subtitle: e.target.value } })} className={inputCls} />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-[#E5DEC3] bg-white p-6 lg:p-8">
        <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.25em] text-[#1B523F]">Customer reviews (3 shown on site)</p>
        <div className="space-y-6">
          {(settings.reviews || []).map((r, i) => (
            <div key={i} className="rounded-xl border border-[#F3EFEA] p-4">
              <label className={labelCls}>Review {i + 1} — quote</label>
              <textarea data-testid={`settings-review-quote-${i}`} rows={2} value={r.quote} onChange={(e) => setAt("reviews", i, "quote", e.target.value)} className={`${inputCls} resize-none`} />
              <div className="mt-3 grid grid-cols-2 gap-3">
                <input data-testid={`settings-review-name-${i}`} value={r.name} onChange={(e) => setAt("reviews", i, "name", e.target.value)} placeholder="Name" className={inputCls} />
                <input data-testid={`settings-review-place-${i}`} value={r.place} onChange={(e) => setAt("reviews", i, "place", e.target.value)} placeholder="Area" className={inputCls} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-[#E5DEC3] bg-white p-6 lg:p-8">
        <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#1B523F]">Before & after photos</p>
        <p className="mb-5 text-xs text-[#4A5A52]">
          Tap Upload to add photos straight from your phone, then press Save changes at the bottom.
        </p>
        <div className="space-y-6">
          {(settings.gallery || []).map((g, i) => (
            <div key={i} className="space-y-3 rounded-xl border border-[#F3EFEA] p-4">
              <div>
                <label className={labelCls}>Photo {i + 1} — label</label>
                <input data-testid={`settings-gallery-label-${i}`} value={g.label} onChange={(e) => setAt("gallery", i, "label", e.target.value)} placeholder="e.g. Kitchen deep clean" className={inputCls} />
              </div>
              <ImageField label="Before photo" value={g.before_url} onChange={(v) => setAt("gallery", i, "before_url", v)} auth={auth} testid={`settings-gallery-before-${i}`} />
              <ImageField label="After photo" value={g.after_url} onChange={(v) => setAt("gallery", i, "after_url", v)} auth={auth} testid={`settings-gallery-after-${i}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-[#E5DEC3] bg-white p-6 lg:p-8">
        <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.25em] text-[#1B523F]">Contact & schedule</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Phone (displayed)</label>
            <input data-testid="settings-phone" value={settings.phone_display} onChange={(e) => setSettings({ ...settings, phone_display: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Company email</label>
            <input data-testid="settings-email" value={settings.email} onChange={(e) => setSettings({ ...settings, email: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Address</label>
            <input data-testid="settings-address" value={settings.address} onChange={(e) => setSettings({ ...settings, address: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Opening hours / schedule</label>
            <input data-testid="settings-hours" value={settings.hours} onChange={(e) => setSettings({ ...settings, hours: e.target.value })} className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Service areas (comma separated)</label>
            <input
              data-testid="settings-areas"
              value={(settings.areas || []).join(", ")}
              onChange={(e) => setSettings({ ...settings, areas: e.target.value.split(",").map((a) => a.trim()).filter(Boolean) })}
              className={inputCls}
            />
          </div>
        </div>
      </div>

      <button
        data-testid="settings-save-button"
        onClick={save}
        disabled={saving}
        className="rounded-full bg-[#0F382A] px-10 py-3.5 text-sm font-semibold text-[#FAF8F5] transition-[background-color,opacity] duration-300 hover:bg-[#1B523F] disabled:opacity-60"
      >
        {saving ? "Saving…" : "Save changes"}
      </button>
    </div>
  );
}

function MarketingKitTab() {
  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-[#E5DEC3] bg-white p-6 lg:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Printer className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.75} />
            <p className="font-serif text-2xl font-light text-[#0F382A]">Service flyer</p>
          </div>
          <a
            data-testid="kit-flyer-link"
            href="/flyer"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-full bg-[#0F382A] px-5 py-2.5 text-xs font-semibold text-[#FAF8F5] transition-colors duration-300 hover:bg-[#1B523F]"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open flyer — print or save as PDF
          </a>
        </div>
        <p className="mt-3 text-sm text-[#4A5A52]">
          A ready-to-print flyer with your live prices, offer and contact details. Open it, then use your browser's print option to print or save as PDF.
        </p>
      </div>

      <div className="rounded-2xl border border-[#E5DEC3] bg-white p-6 lg:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <MessageCircle className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.75} />
            <p className="font-serif text-2xl font-light text-[#0F382A]">WhatsApp message template</p>
          </div>
          <button
            data-testid="kit-whatsapp-copy"
            onClick={() => copyText(WHATSAPP_TEMPLATE, "WhatsApp template")}
            className="flex items-center gap-2 rounded-full border border-[#0F382A] px-5 py-2.5 text-xs font-semibold text-[#0F382A] transition-colors duration-300 hover:bg-[#0F382A] hover:text-[#FAF8F5]"
          >
            <Copy className="h-3.5 w-3.5" />
            Copy message
          </button>
        </div>
        <p className="mt-3 text-sm text-[#4A5A52]">Replace {"{name}"} with the client's name, then paste into WhatsApp.</p>
        <pre data-testid="kit-whatsapp-text" className="mt-4 whitespace-pre-wrap rounded-xl bg-[#FAF8F5] p-5 font-sans text-sm leading-relaxed text-[#121A16]">{WHATSAPP_TEMPLATE}</pre>
      </div>

      <div className="rounded-2xl border border-[#E5DEC3] bg-white p-6 lg:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.75} />
            <p className="font-serif text-2xl font-light text-[#0F382A]">Contract template</p>
          </div>
          <button
            data-testid="kit-contract-copy"
            onClick={() => copyText(CONTRACT_TEMPLATE, "Contract template")}
            className="flex items-center gap-2 rounded-full border border-[#0F382A] px-5 py-2.5 text-xs font-semibold text-[#0F382A] transition-colors duration-300 hover:bg-[#0F382A] hover:text-[#FAF8F5]"
          >
            <Copy className="h-3.5 w-3.5" />
            Copy contract
          </button>
        </div>
        <p className="mt-3 text-sm text-[#4A5A52]">Fill in the {"{placeholders}"}, paste into a document, and print two copies for signing.</p>
        <pre data-testid="kit-contract-text" className="mt-4 whitespace-pre-wrap rounded-xl bg-[#FAF8F5] p-5 font-sans text-sm leading-relaxed text-[#121A16]">{CONTRACT_TEMPLATE}</pre>
      </div>
    </div>
  );
}

function PasswordTab({ auth }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next }, auth);
      toast.success("Password updated. Use the new one next time you sign in.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form data-testid="password-change-form" onSubmit={submit} className="max-w-md rounded-2xl border border-[#E5DEC3] bg-white p-6 lg:p-8">
      <label className={labelCls}>Current password</label>
      <input data-testid="password-current-input" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className={inputCls} />
      <label className={`${labelCls} mt-5`}>New password (min 8 characters)</label>
      <input data-testid="password-new-input" type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} className={inputCls} />
      <label className={`${labelCls} mt-5`}>Confirm new password</label>
      <input data-testid="password-confirm-input" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
      <button
        data-testid="password-change-submit"
        type="submit"
        disabled={saving}
        className="mt-6 rounded-full bg-[#0F382A] px-10 py-3.5 text-sm font-semibold text-[#FAF8F5] transition-[background-color,opacity] duration-300 hover:bg-[#1B523F] disabled:opacity-60"
      >
        {saving ? "Updating…" : "Update password"}
      </button>
    </form>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("enquiries");
  const [user, setUser] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [applications, setApplications] = useState([]);
  const [handoffs, setHandoffs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [availability, setAvailability] = useState(null);

  const token = localStorage.getItem("ds_admin_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadAvailability = () =>
    axios.get(`${API}/availability`).then((r) => setAvailability(r.data)).catch(() => {});

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    axios.get(`${API}/auth/me`, auth)
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("ds_admin_token");
        navigate("/admin/login");
      });
    axios.get(`${API}/enquiries`, auth).then((r) => setEnquiries(r.data)).catch(() => {});
    axios.get(`${API}/applications`, auth).then((r) => setApplications(r.data)).catch(() => {});
    axios.get(`${API}/assistant/handoffs`, auth).then((r) => setHandoffs(r.data)).catch(() => {});
    axios.get(`${API}/settings`).then((r) => setSettings(r.data)).catch(() => {});
    loadAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem("ds_admin_token");
    navigate("/admin/login");
  };

  const setItemStatus = (setter) => (id, status) =>
    setter((items) => items.map((it) => ((it.id || it.session_id) === id ? { ...it, status } : it)));

  if (!user)
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF8F5]">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#4A5A52]">Loading…</p>
      </div>
    );

  const badge = (n) =>
    n > 0 ? (
      <span className="ml-2 rounded-full bg-[#D4AF37] px-2 py-0.5 text-[10px] font-bold text-[#0B1D16]">{n}</span>
    ) : null;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAF8F5]">
      <header className="border-b border-[#E5DEC3] bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div>
            <p className="font-serif text-2xl font-medium text-[#0F382A]">Don Sparkle<span className="text-[#D4AF37]">.</span></p>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4A5A52]">Signed in as {user.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-[#4A5A52] underline decoration-[#D4AF37] underline-offset-4 hover:text-[#0F382A]">View site</a>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-[#E5DEC3] px-5 py-2.5 text-sm font-semibold text-[#0F382A] transition-colors duration-300 hover:border-[#0F382A]"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.75} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-8 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={t.testid}
              onClick={() => setTab(t.id)}
              className={`rounded-full px-5 py-2.5 text-sm font-medium transition-[background-color,color] duration-300 ${
                tab === t.id ? "bg-[#0F382A] text-[#FAF8F5]" : "bg-white text-[#4A5A52] hover:text-[#0F382A]"
              }`}
            >
              {t.label}
              {t.id === "enquiries" && badge(enquiries.filter((e) => e.status !== "done" && e.status !== "cancelled").length)}
              {t.id === "applications" && badge(applications.filter((a) => a.status !== "done").length)}
              {t.id === "handoffs" && badge(handoffs.filter((h) => h.status === "pending").length)}
            </button>
          ))}
        </div>

        {tab === "enquiries" && <EnquiriesTab items={enquiries} auth={auth} onStatusChange={setItemStatus(setEnquiries)} slots={availability?.slots || []} />}
        {tab === "applications" && <ApplicationsTab items={applications} auth={auth} onStatusChange={setItemStatus(setApplications)} />}
        {tab === "handoffs" && <HandoffsTab items={handoffs} auth={auth} onStatusChange={setItemStatus(setHandoffs)} />}
        {tab === "availability" && <AvailabilityTab availability={availability} reload={loadAvailability} auth={auth} />}
        {tab === "settings" && <SettingsTab settings={settings} setSettings={setSettings} auth={auth} />}
        {tab === "kit" && <MarketingKitTab />}
        {tab === "password" && <PasswordTab auth={auth} />}
      </div>
    </div>
  );
}
